<template>
  <v-container fluid>
    <v-form @submit.prevent="submit" validate-on="input">
      <v-card elevation="1" class="pa-4 pt-6 mt-n4 ml-n4">
        <v-row>
          <v-col cols="6">
            <v-text-field
              autofocus
              v-model="getAHUState.friendlyName"
              label="AHU Friendly Name"
              :rules="[(v) => !!v || 'Name is required']"
              required
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col sm="6">
            <SiteLocation
              :siteId="siteId"
              :selectedSiteLocationId="getAHUState.siteLocationId"
              :controlType="'AHU'"
              @sitelocationselected="siteLocationSelected"
            />
          </v-col>
          <v-col sm="6">
            <v-autocomplete
              :items="equipmentsList"
              :loading="isEquipmentsLoading"
              :disabled="!equipmentsList.length"
              item-value="equipmentId"
              item-title="equipmentName"
              clearable
              :model-value="getAHUState.equipmentId"
              @update:model-value="(item) => (getAHUState.equipmentId = item)"
              label="Equipment"
              persistent-hint
              variant="outlined"
              density="compact"
            />
          </v-col>
        </v-row>
        <v-row class="mt-n4 ml-n5">
          <v-col cols="6">
            <v-col>
              <v-btn type="submit" color="primary" :loading="isLoading">
                Save
              </v-btn>
              <v-btn
                class="ml-5"
                @click="router.push({ name: 'AHUsPortfolio' })"
              >
                Cancel
              </v-btn>
            </v-col>
          </v-col>
        </v-row>
      </v-card>
    </v-form>
  </v-container>
</template>

<script setup lang="ts">
import { computed, ref, inject, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';

import { ToastService } from '@/plugins/toasts';
import { ToastOptions } from '@/Core.UI.Domain/types/Toast';

import SiteLocation from '@/modules/site/site_location/_components/SiteLocation.vue';

import API from '@/Core.Service.Domain/Controls/AHUs/API/index.ts';
import EquipmentAPI from '@/modules/site/equipment/_api';

import { IAHU } from '@/Core.Service.Domain/Controls/AHUs/types/AHU.ts';
import { IEquipment } from '@/Core.Service.Domain/Controls/Controls.Common/types/Equipment.ts';

import { keepSelectedKeys } from '@/Core.Patterns/Factory';

const equipmentsList = ref<IEquipment[]>([]);
const isEquipmentsLoading = ref(false);
const equipmentId = ref<Partial<IAHU>>();
onMounted(async () => {
  try {
    isEquipmentsLoading.value = true;
    equipmentsList.value = await EquipmentAPI.getEquipmentForSite(
      getAHUState.value.siteId
    );
    equipmentId.value = await API.getAHUById(getAHUState.value.deviceId);
    if (equipmentId.value && equipmentId.value.equipmentId) {
      getAHUState.value.equipmentId = equipmentId.value.equipmentId;
    }
  } catch (error) {
    return Promise.reject(error);
  } finally {
    isEquipmentsLoading.value = false;
  }
});

const router = useRouter();

const store = useStore();
const getAHUState = computed<IAHU>(
  () => store.getters['ControlsStore/AHUsStore/getEditAHU']
);

const route = useRoute();
const siteId = computed(() => route.params.siteId);

const siteLocationSelected = (siteLocationId: { id: number }) => {
  getAHUState.value.siteLocationId = siteLocationId.id;
};

const toast = inject<ToastService>('toast')!;
const isLoading = ref(false);

const sanitizedAHU = ref<Partial<IAHU>>();
const submit = async (
  event: PromiseLike<{ valid: boolean }> | { valid: boolean }
) => {
  const { valid } = await event;
  if (!valid) {
    return Promise.reject({
      Error: 'Please fill in all required fields',
    });
  }

  isLoading.value = true;
  try {
    sanitizedAHU.value = keepSelectedKeys(
      getAHUState.value as unknown as Record<string, unknown>,
      ['deviceId', 'friendlyName', 'siteLocationId', 'equipmentId', 'isActive']
    );

    await API.updateAHUById(sanitizedAHU.value as IAHU);
    const options: ToastOptions = {
      title: 'Success',
      message: `AHU settings successfully updated.`,
      status: 'success',
    };
    toast.show(options);
    router.push({ name: 'AHUsPortfolio' });
  } catch (error) {
    return Promise.reject(error);
  } finally {
    isLoading.value = false;
  }
};
</script>
